/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
  CollapseButton,
  ConfirmDialog,
  WbCard,
  WbDockableGroup,
  WbDockablePanel,
  WbDockableResizeHandle,
} from '@agilelab/plugin-wb-platform';
import {
  errorApiRef,
  featureFlagsApiRef,
  useAnalytics,
  useApi,
  useRouteRefParams,
} from '@backstage/core-plugin-api';
import { JsonObject } from '@backstage/types';
import {
  Box,
  Button,
  makeStyles,
  Theme,
  Typography,
  useTheme,
} from '@material-ui/core';
import { FormProps, IChangeEvent, withTheme } from '@rjsf/core';
import { Theme as MuiTheme } from '@rjsf/material-ui';
import cloneDeep from 'lodash/cloneDeep';
import {
  Fragment,
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { LayoutOptions } from '../../layouts';
import { selectedTemplateRouteRef } from '../../routes';
import { ScaffolderThemeProvider } from '../ScaffolderThemeProvider/ScaffolderThemeProvider';
import { StepperElement } from '../StepperElement/StepperElement';
import { DefaultFieldTemplate } from '../fields';
import { DefaultArrayFieldTemplate } from '../fields/ArrayFieldTemplate/DefaultArrayFieldTemplate';
import { DefaultObjectFieldTemplate } from '../fields/ArrayFieldTemplate/DefaultObjectFieldTemplate';
import * as widgetOverrides from './WidgetOverrides';
import * as templateOverrides from './TemplateOverrides';
import { transformErrors } from './errorsOverrides';
import { transformSchemaToProps } from './schema';
import { AdditionalStep, Step } from './utils';
import { WittyFormContextProvider } from '@agilelab/plugin-wb-witty-react';
import { FormExtraConfigContextProvider } from '../../contexts/FormExtraConfigContext';
import { FieldExtensionOptions, WmUiSchema } from '../../extensions';
import { ProcessingDialog } from './Modals/ProcessingDialog';
import validator from '@rjsf/validator-ajv8';
import { useDefaultFields } from './useDefaultFields';

const useStyles = makeStyles((theme: Theme) => ({
  form: { paddingRight: '8px', paddingLeft: '8px' },
  box: { minWidth: '500px', width: '100%' },
  actionsContainer: { display: 'flex', columnGap: theme.spacing(1) },
}));
const Form = withTheme(MuiTheme);
type Props = {
  /**
   * Steps for the form, each contains title and form schema
   */
  steps: Step[];
  formData: Record<string, any>;
  initialStep?: number;
  onChange: (e: IChangeEvent) => void;
  widgets?: FormProps<any>['widgets'];
  fields?: FieldExtensionOptions<any, any>[];
  layouts: LayoutOptions[];
  additionalSteps?: AdditionalStep[];
  rootStepperStyle?: JsonObject;
  stepContentStyle?: JsonObject;
  viewStepperLabel?: boolean;
  customBackButton?: JSX.Element;
  customNextButton?: JSX.Element;
  customOnSubmitFn?: () => Promise<void>;
  previewStep?: number;
};

export function getUiSchemasFromSteps(steps: Step[]): WmUiSchema[] {
  const uiSchemas: Array<WmUiSchema> = [];
  steps.forEach(step => {
    const schemaProps = step.schema.properties as JsonObject;
    for (const key in schemaProps) {
      if (schemaProps.hasOwnProperty(key)) {
        const uiSchema =
          schemaProps[key] && typeof schemaProps[key] === 'object'
            ? (schemaProps[key] as WmUiSchema)
            : {};
        uiSchema.name = key;
        uiSchemas.push(uiSchema);
      }
    }
  });
  return uiSchemas;
}

export const MultiStepJsonEditForm = (props: Props) => {
  const {
    formData,
    onChange,
    fields,
    widgets,
    layouts,
    additionalSteps,
    rootStepperStyle,
    customOnSubmitFn,
    initialStep,
    previewStep,
  } = props;
  const { templateName } = useRouteRefParams(selectedTemplateRouteRef);
  const theme = useTheme();
  const ref: MutableRefObject<any> = useRef(null);
  const submitRef = useRef<any>(null);
  const analytics = useAnalytics();
  const [activeStep, setActiveStep] = useState(initialStep ?? 0);
  const [openDialog, setOpenDialog] = useState(false);
  const errorApi = useApi(errorApiRef);
  const featureFlagApi = useApi(featureFlagsApiRef);
  const featureFlagKey = 'backstage:featureFlag';
  const toolbarHeight = useRef(64);

  const usedFields = useDefaultFields(fields);

  useEffect(() => {
    const witboostToolbar = document.getElementById('witboost-toolbar');
    if (witboostToolbar) {
      toolbarHeight.current = witboostToolbar.clientHeight;
    }
  });

  const filterOutProperties = (step: Step): Step => {
    const filteredStep = cloneDeep(step);
    const removedPropertyKeys: Array<string> = [];
    if (filteredStep.schema.properties) {
      filteredStep.schema.properties = Object.fromEntries(
        Object.entries(filteredStep.schema.properties).filter(
          ([key, value]) => {
            if (value && value[featureFlagKey]) {
              if (featureFlagApi.isActive(value[featureFlagKey])) {
                return true;
              }
              removedPropertyKeys.push(key);
              return false;
            }
            return true;
          },
        ),
      );

      // remove the feature flag property key from required if they are not active
      filteredStep.schema.required = Array.isArray(filteredStep.schema.required)
        ? filteredStep.schema.required?.filter(
            r => !removedPropertyKeys.includes(r as string),
          )
        : filteredStep.schema.required;
    }
    return filteredStep;
  };

  const steps = props.steps
    .filter(step => {
      const featureFlag = step.schema[featureFlagKey];
      return (
        typeof featureFlag !== 'string' || featureFlagApi.isActive(featureFlag)
      );
    })
    .map(filterOutProperties);

  // hardcoded for EditorWizardPage, shows the processing modal when true
  // TODO remove hardcoded behaviour for EditorWizardPage, should be handled from there
  const [showProcessing, setShowProcessing] = useState(false);

  // this is the step we want to go to but we must first submit the form and check that validation is succesful
  const desiredStepRef = useRef(0);

  // wether the current active step is a form step, false if current step is an additional step
  const formStepActive = activeStep < steps.length;

  // we currently have specific behaviour for EditorWizardPage and we determine it based on the number of additionalSteps
  // TODO remove hardcoded behaviour for EditorWizardPage, should be handled from there
  const isEditorWizardPage = additionalSteps?.length === 3;

  // hardcoded for EditorWizardPage, where the processing step is always present and is the second last step
  // TODO remove hardcoded behaviour for EditorWizardPage, should be handled from there
  const processingStepIndex = steps.length + (additionalSteps?.length ?? 0) - 2;

  const handleNext = (newStep: number) => {
    const step = newStep;

    if (step === processingStepIndex && additionalSteps?.[1]) {
      setShowProcessing(true);
    }

    if (showProcessing) setShowProcessing(false);
    setActiveStep(step);
    analytics.captureEvent('click', `Next Step (${step})`);
  };

  const [error, setError] = useState<any>(null);
  const [stepperCollapsed, setStepperCollapsed] = useState(false);

  const { currentFormStep, currentAdditionalStepIndex } = useMemo(() => {
    if (formStepActive) {
      return {
        currentFormStep: steps[activeStep],
        currentAdditionalStepIndex: null,
      };
    }
    return {
      currentFormStep: null,
      currentAdditionalStepIndex: activeStep - steps.length,
    };
  }, [activeStep, steps, formStepActive]);

  const {
    title: additionalTitle,
    component,
    additionalActions,
    backButtonText,
    nextButtonText,
    disableStepButtons,
    customDisableStepButtons,
    resetButtonText,
    analyticsEvent,
    onSaveButtonClick,
    nextAction,
  } = additionalSteps?.[currentAdditionalStepIndex ?? -1] ?? {};

  // hardcoded for EditorWizardPage, where the processing is provided as nextAction of the first additional step, Review and Information
  // TODO remove hardcoded behaviour for EditorWizardPage, should be handled from there
  const startProcessing = () => {
    if (additionalSteps?.[0].nextAction) {
      additionalSteps?.[0].nextAction();
    }
  };

  const styles = useStyles();

  const handleStep = (e: number) => {
    desiredStepRef.current = e;
    if (currentFormStep) {
      submitRef?.current.click();
    } else if (e === processingStepIndex) {
      // hardcoded for starting process when entering processingStep of EditorWizardPage
      // TODO remove hardcoded behaviour for EditorWizardPage, should be handled from there
      startProcessing();
      if (analyticsEvent) {
        analytics.captureEvent(
          analyticsEvent,
          formData.name || `new ${templateName}`,
        );
      }
      handleNext(e);
    } else setActiveStep(e);
  };

  const title = currentFormStep?.title ?? additionalTitle ?? '';

  const lastAdditionalStepActive =
    activeStep === steps.length + (additionalSteps?.length ?? 0) - 1;

  return (
    <WbCard
      title="Edit template wizard"
      cardStyle={{ height: '100%' }}
      actions={
        <Box className={styles.actionsContainer}>
          {activeStep <= steps.length && (
            <>
              <Button
                form={`form-${title}`}
                variant="outlined"
                color="primary"
                disabled={activeStep === 0}
                onClick={() => {
                  handleStep(activeStep - 1);
                }}
                type="submit"
              >
                Back
              </Button>
              <Button
                form={`form-${title}`}
                variant="outlined"
                color="primary"
                type="submit"
                disabled={activeStep === steps.length}
                onClick={() => handleStep(activeStep + 1)}
              >
                Next
              </Button>
            </>
          )}
          {/* this is hardcoded back button for last additional step of EditorWizardPage */}
          {/* TODO remove hardcoded behaviour for EditorWizardPage, should be handled from there */}
          {lastAdditionalStepActive && isEditorWizardPage && (
            <>
              <Button
                form={`form-${title}`}
                variant="outlined"
                color="primary"
                disabled={activeStep === 0}
                onClick={() => setOpenDialog(true)}
                type="submit"
              >
                Back
              </Button>
            </>
          )}
          {additionalActions?.map(action => (
            <Fragment key={action.key}>{action}</Fragment>
          ))}
          {((formStepActive && previewStep) || onSaveButtonClick) && (
            <Button
              variant="contained"
              color="primary"
              type="submit"
              form={`form-${title}`}
              onClick={() => {
                if (onSaveButtonClick) {
                  onSaveButtonClick({
                    setStep: handleStep,
                    setAdditionalStep: additionalStep =>
                      handleStep(steps.length + additionalStep),
                  });
                  return;
                }
                if (previewStep) handleStep(previewStep);
              }}
              disabled={
                customDisableStepButtons?.disableNext ?? disableStepButtons
              }
            >
              {onSaveButtonClick ? 'Save' : 'Preview and Save'}
            </Button>
          )}
        </Box>
      }
    >
      <ScaffolderThemeProvider>
        <WbDockableGroup direction="horizontal">
          {/* show sections panel and resize handle only for first additionalStep, since <StepperElement /> is
          hardcoded to only show first as section */}
          {/* TODO this should not be hardcoded, should be handled outside */}
          {activeStep <= steps.length && (
            <WbDockablePanel
              showCollapsedContent
              expandOnClick={false}
              expandOnResize={false}
              isCardChildren
              collapsible
              maxSize={40}
              minSize={15}
              defaultSize={30}
              tab={{
                isCollapsed: stepperCollapsed,
                setCollapsed: () => setStepperCollapsed(prev => !prev),
                title: 'Sections',
                direction: 'horizontal',
              }}
            >
              <WbCard
                title="Sections"
                leading={
                  stepperCollapsed ? (
                    <></>
                  ) : (
                    <CollapseButton
                      background={false}
                      onClick={() => {
                        setStepperCollapsed(true);
                      }}
                    />
                  )
                }
                headerStyle={{
                  background: 'none',
                  paddingLeft: theme.spacing(3),
                  marginTop: theme.spacing(1),
                  display: stepperCollapsed ? 'none' : 'flex',
                }}
                toolsStyle={{
                  border: 'none',
                }}
                toolsBarStyle={{
                  width: '100%',
                }}
                cardStyle={{
                  height: '100%',
                  boxShadow: 'none',
                }}
              >
                <StepperElement
                  isCollapsed={stepperCollapsed}
                  steps={steps}
                  currentStep={
                    currentFormStep ||
                    additionalSteps?.[currentAdditionalStepIndex ?? -1] ||
                    null
                  }
                  activeStep={activeStep}
                  ref={ref}
                  setActiveStep={handleStep}
                  additionalSteps={additionalSteps}
                  rootStepperStyle={rootStepperStyle}
                />
              </WbCard>
            </WbDockablePanel>
          )}
          {activeStep <= steps.length && (
            <WbDockableResizeHandle
              direction="horizontal"
              showIconOnHover
              divider
            />
          )}
          <WbDockablePanel>
            {/* shown when currentFormStep defined, currentFormStep means it is a step from the form rather than an additionalStep */}
            {currentFormStep && (
              <>
                <Typography
                  color="textPrimary"
                  style={{ padding: '8px' }}
                  variant="h3"
                >
                  {currentFormStep.title}
                </Typography>
                <FormExtraConfigContextProvider debounceMs={300}>
                  <WittyFormContextProvider
                    onChange={onChange}
                    formData={formData}
                    formDefinition={currentFormStep?.schema}
                  >
                    <Form
                      id={`form-${currentFormStep.title ?? additionalTitle}`}
                      extraErrors={error}
                      className={styles.form}
                      templates={{
                        ArrayFieldTemplate: DefaultArrayFieldTemplate,
                        ObjectFieldTemplate: DefaultObjectFieldTemplate,
                        FieldTemplate: DefaultFieldTemplate,
                        ...templateOverrides,
                      }}
                      fields={usedFields}
                      widgets={{ ...widgetOverrides, ...widgets }}
                      noHtml5Validate
                      formData={formData}
                      onChange={onChange}
                      showErrorList={false}
                      noValidate={currentFormStep?.noValidate}
                      validator={validator}
                      onSubmit={async e => {
                        const desiredStep = desiredStepRef.current;
                        if (currentFormStep?.asyncValidate) {
                          const validationResult =
                            await currentFormStep?.asyncValidate(formData);
                          if (validationResult) {
                            const keysWithErrors = Object.keys(
                              validationResult,
                            ).filter(
                              key => validationResult[key]?.__errors?.length,
                            );

                            if (
                              keysWithErrors.length === 0 &&
                              e.errors.length === 0
                            ) {
                              if (typeof customOnSubmitFn === 'function') {
                                await customOnSubmitFn();
                              }
                              handleNext(desiredStep);
                            } else {
                              setError(validationResult);
                            }
                          } else {
                            if (e.errors.length === 0) {
                              if (typeof customOnSubmitFn === 'function') {
                                await customOnSubmitFn();
                              }
                              handleNext(desiredStep);
                            }
                          }
                        } else {
                          if (e.errors.length === 0) {
                            if (typeof customOnSubmitFn === 'function') {
                              await customOnSubmitFn();
                            }
                            handleNext(desiredStep);
                          }
                        }
                        if (desiredStep === processingStepIndex) {
                          try {
                            if (additionalSteps?.[0].nextAction)
                              await additionalSteps?.[0].nextAction();
                            if (analyticsEvent) {
                              analytics.captureEvent(
                                analyticsEvent,
                                formData.name || `new ${templateName}`,
                              );
                            }
                            handleNext(desiredStep);
                          } catch (err) {
                            errorApi.post(err);
                          }
                        }
                      }}
                      {...currentFormStep}
                      {...transformSchemaToProps(
                        currentFormStep?.schema ?? {},
                        layouts,
                      )}
                      formContext={formData}
                      transformErrors={transformErrors}
                    >
                      <>
                        <Button
                          form={`form-${title}`}
                          variant="contained"
                          color="primary"
                          style={{ display: 'none' }}
                          ref={submitRef}
                          type="submit"
                        />
                      </>
                    </Form>
                  </WittyFormContextProvider>
                </FormExtraConfigContextProvider>
              </>
            )}
            {/* if an additional Step is defined BUT must not be processing, it will be shown here */}
            {/* TODO remove processingStep hardcoded logic, should be handled outside */}
            {currentAdditionalStepIndex !== null &&
              activeStep !== processingStepIndex && (
                <Box
                  style={{
                    width:
                      additionalTitle ===
                      additionalSteps?.[additionalSteps?.length - 1].title
                        ? '100%'
                        : 'auto',
                  }}
                >
                  {activeStep === steps.length && (
                    <Typography
                      color="textPrimary"
                      style={{ padding: '8px' }}
                      variant="h3"
                    >
                      {additionalTitle}
                    </Typography>
                  )}
                  {component}
                </Box>
              )}
          </WbDockablePanel>
        </WbDockableGroup>
      </ScaffolderThemeProvider>
      {/* processing step modal for EditorWizardPage */}
      {/* TODO remove hardcoded behaviour for EditorWizardPage, should be handled from there */}
      <ProcessingDialog
        open={showProcessing}
        onClose={() => setShowProcessing(false)}
        onBack={() => {
          if (previewStep) setActiveStep(previewStep);
        }}
        isError={additionalSteps?.[1]?.taskFailed}
        isSuccess={
          !additionalSteps?.[1]?.taskFailed &&
          !additionalSteps?.[1]?.disableStepButtons
        }
        submitButton={
          <Button
            variant="contained"
            color="primary"
            disabled={
              additionalSteps?.[1]?.customDisableStepButtons?.disableNext ??
              additionalSteps?.[1]?.disableStepButtons
            }
            type="submit"
            onClick={async () => {
              try {
                if (nextAction) await nextAction();
                if (analyticsEvent) {
                  analytics.captureEvent(
                    analyticsEvent,
                    formData.name || `new ${templateName}`,
                  );
                }
                setShowProcessing(false);
                setActiveStep(
                  steps?.length + (additionalSteps?.length ?? 0) - 1,
                );
              } catch (err) {
                errorApi.post(err);
              }
            }}
          >
            {nextButtonText}
          </Button>
        }
      >
        <Box style={{ minWidth: '100%', height: '60vh' }}>
          {additionalSteps?.[1]?.component}
          {(resetButtonText || nextButtonText || backButtonText) && (
            <Box mb={3} />
          )}
        </Box>
      </ProcessingDialog>
      {/* TODO remove hardcoded behaviour for EditorWizardPage, should be handled from there */}
      {lastAdditionalStepActive && isEditorWizardPage ? (
        <>
          <ConfirmDialog
            title="Go back to the Wizard"
            description="Are you sure you want to go back and discard any modifications made on this page?"
            open={openDialog}
            onClose={() => {
              setOpenDialog(false);
            }}
            onConfirm={() => {
              setOpenDialog(false);
              setActiveStep(steps.length);
            }}
            confirmButtonText="Yes, go back"
          />
        </>
      ) : (
        <span />
      )}
    </WbCard>
  );
};
